

import { useState, useEffect, useContext } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Avatar from "@mui/material/Avatar";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service";
import UserService from "services/user-service";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import MDButton from "components/MDButton";
import { AuthContext } from "context";

import Plan from "layouts/profile/components/Subscription/plan";
import UpgradeBanner from "layouts/components/Upgrade/banner";
import FreeTrialBanner from "layouts/components/Upgrade/freeTrialBanner";
import ExitLiveDemo from "layouts/components/Upgrade/exitLiveDemo"; // [NEW]


function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const authContext = useContext(AuthContext);
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  let navigate = useNavigate();

  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [openExitDialog, setOpenExitDialog] = useState(false); // [NEW]

  // State for Subscription Data
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [loading, setLoading] = useState(true);

  const [subscriptionHistory, setSubscriptionHistory] = useState([]);

  // [NEW] Profile Menu State
  const [workspaceProfile, setWorkspaceProfile] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  // [NEW] Switch Workspace State
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  /**
   * Fetch Profile Data (for Avatar)
   */
  const fetchWorkspaceProfile = async () => {
    try {
      const response = await AuthService.getProfile();
      setWorkspaceProfile(response.data.attributes);
    } catch (error) {
      console.error("Error fetching workspace profile:", error);
    }
  };

  useEffect(() => {
    fetchWorkspaceProfile();

    // Listen for profile updates (syncs with Account/Header changes)
    const onProfileUpdated = () => fetchWorkspaceProfile();
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  const handleProfileMenuOpen = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
    setWorkspaceMenuAnchor(null);
  };

  /**
   * [NEW] Workspace Switching Logic
   */
  const handleWorkspaceMenuOpen = async (event) => {
    setWorkspaceMenuAnchor(event.currentTarget);
    setLoadingWorkspaces(true);
    try {
      const response = await UserService.fetchUserWorkspaces();
      // Robust check: response could be array or in data
      const list = Array.isArray(response) ? response : (response.data || []);
      setWorkspaces(list);
    } catch (error) {
      console.error("Failed to fetch workspaces", error);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);

  const handleSwitchWorkspace = async (workspaceId) => {
    try {
      await UserService.switchActiveWorkspace(workspaceId);

      // ✅ Sync activeWorkspaceId to localStorage for immediate API reflects
      localStorage.setItem("activeWorkspaceId", workspaceId);

      // 🔥 Soft refresh: notify other components (Header, Workspace list) to update
      window.dispatchEvent(new CustomEvent("profile-updated"));

      // Close the profile/workspace menu
      handleProfileMenuClose();
    } catch (error) {
      console.error("Failed to switch workspace", error);
    }
  };


  /**
   * Fetch Subscription Data
   */
  const fetchWorkspaceSubscription = async () => {
    try {
      const response = await WorkspaceService.fetchWorkspaceSubscription();

      // Extract Active Subscription
      const activeSubscription = response.find(sub => sub.status.toLowerCase() === "active");

      if (activeSubscription) {
        setActiveSubscription({
          code: activeSubscription.code || "N/A",
          plan: activeSubscription.plan || "N/A",
          billingCycle: activeSubscription.billing_cycle || "N/A",
          renewalDate: activeSubscription.next_renewal_date
            ? new Date(activeSubscription.next_renewal_date).toLocaleDateString()
            : "N/A",
          status: activeSubscription.status.charAt(0).toUpperCase() + activeSubscription.status.slice(1) || "N/A",
          autoRenewal: activeSubscription.auto_renew || false,
        });
      } else {
        setActiveSubscription(null);
      }

      // Extract Pending Subscription
      const pendingSub = response.find(sub => sub.status.toLowerCase() === "pending");

      if (pendingSub) {
        setPendingSubscription({
          code: pendingSub.code || "N/A",
          plan: pendingSub.plan || "N/A",
          billingCycle: pendingSub.billing_cycle || "N/A",
          renewalDate: pendingSub.next_renewal_date
            ? new Date(pendingSub.next_renewal_date).toLocaleDateString()
            : "N/A",
          status: pendingSub.status.charAt(0).toUpperCase() + pendingSub.status.slice(1) || "N/A",
          autoRenewal: pendingSub.auto_renew || false,
        });
      } else {
        setPendingSubscription(null);
      }

      // Store full subscription history
      setSubscriptionHistory(response);
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceSubscription();
  }, []);

  // Assuming the currentPlan is coming from activeSubscription (adjust if your prop is different)
  // const currentPlan = activeSubscription?.plan || "";
  // const isTrialUser = currentPlan.toLowerCase() === "trial";

  const currentPlan = activeSubscription?.plan || "";
  const renewalDateStr = activeSubscription?.renewalDate; // Might be in DD/MM/YYYY

  let isTrialUser = false;

  if (currentPlan.toLowerCase() === "trial" && renewalDateStr) {

    // Parse DD/MM/YYYY safely
    const [day, month, year] = renewalDateStr.split("/").map(Number);
    const renewalDate = new Date(year, month - 1, day);

    const now = new Date();
    const diffInMs = renewalDate - now;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInDays <= 1) { // within today or next day
      isTrialUser = true;
    }
  }


  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  // [NEW] Render Profile Menu
  const renderProfileMenu = () => (
    <Menu
      anchorEl={profileMenuAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(profileMenuAnchor)}
      onClose={handleProfileMenuClose}
      sx={{ mt: 1 }}
    >
      <MenuItem onClick={() => { handleProfileMenuClose(); navigate("/profile"); }}>
        <ListItemIcon>
          <Icon fontSize="small">settings</Icon>
        </ListItemIcon>
        Settings
      </MenuItem>

      {/* Switch Workspace Trigger */}
      <MenuItem onClick={handleWorkspaceMenuOpen}>
        <ListItemIcon>
          <Icon fontSize="small">swap_horiz</Icon>
        </ListItemIcon>
        Switch Workspace
      </MenuItem>

      {/* Nested Workspace Menu */}
      <Menu
        anchorEl={workspaceMenuAnchor}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(workspaceMenuAnchor)}
        onClose={handleWorkspaceMenuClose}
        sx={{ mt: 0, mr: 1 }}
      >
        {loadingWorkspaces ? (
          <MenuItem disabled>Loading...</MenuItem>
        ) : workspaces.length > 0 ? (
          workspaces.map((ws) => (
            <MenuItem
              key={ws.workspace_id?._id || ws._id}
              onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
              selected={ws.is_active}
            >
              {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No other workspaces</MenuItem>
        )}
      </Menu>

      <Divider />
      <MenuItem onClick={() => { handleProfileMenuClose(); handleLogOut(); }}>
        <ListItemIcon>
          <Icon fontSize="small">logout</Icon>
        </ListItemIcon>
        {sessionStorage.getItem('demo_role_cache') === "demo_viewer" ? "Exit Live Demo" : "Log Out"}
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const handleLogOut = async () => {
    const isDemoInfo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";

    // [NEW] Intercept logout for demo users
    if (isDemoInfo) {
      setOpenExitDialog(true);
      return;
    }

    // Always perform logout to clear session/cookies
    await AuthService.logout();
    authContext.logout();
    // Redirect handled by context/router usually, or manual
  };

  // [NEW] Confirm Exit (Convert to Trial)
  const handleExitDemoConfirm = () => {
    // Redirect to registration
    window.location.href = `${process.env.REACT_APP_URL}/auth/register`;
  };

  // [NEW] Cancel Exit (Actually Logout)
  const handleExitDemoLogout = async () => {
    setOpenExitDialog(false);

    // Clear demo-specific session storage
    sessionStorage.removeItem('demo_role_cache');
    sessionStorage.removeItem('demo_completed_tutorials');
    sessionStorage.removeItem('demo_banner_triggered');
    sessionStorage.removeItem('demo_stash_attempts'); // [NEW] Reset demo attempts
    sessionStorage.removeItem("insight_hub_welcome_shown"); // [NEW] Reset Insight Hub welcome
    sessionStorage.removeItem("platform_scout_intro_shown"); // [NEW] Reset Platform Scout welcome
    localStorage.removeItem("social_studio_demo_completed"); // [NEW] Reset top banner logic

    // Perform logout
    await AuthService.logout();
    authContext.logout();

    // Redirect to landing
    window.location.href = process.env.REACT_APP_URL_LANDING || "https://comparedly.com";
  };

  const handleSubscriptionUpdate = () => {
    // Optional: Re-fetch subscription after user updates plan
    console.log("Subscription updated");
  };

  // [NEW] Free Trial Banner Logic (Live Demo Only)
  const [showFreeTrialBanner, setShowFreeTrialBanner] = useState(false);

  useEffect(() => {
    // 1. Check if Live Demo User
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (!isDemo) return;

    // 2. Check Persisted State
    const hasTriggered = sessionStorage.getItem('demo_banner_triggered') === "true";
    if (hasTriggered) {
      setShowFreeTrialBanner(true);
      return;
    }

    // 3. Check Page (Only trigger initially on Insight Hub)
    const isInsightHub = route.includes("insight-hub");
    if (!isInsightHub) {
      setShowFreeTrialBanner(false);
      return;
    }

    // 4. Scroll Listener (Trigger Once)
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      if (scrollTop > scrollHeight * 0.4) {
        setShowFreeTrialBanner(true);
        sessionStorage.setItem('demo_banner_triggered', "true"); // Persist
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [route]); // Re-run if route changes


  return (
    <>
      <AppBar
        // position={absolute ? "absolute" : navbarType}
        // color="inherit"
        // sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => ({
          ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
        })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          {/* Left Side (Toggle + Breadcrumbs) */}
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <Breadcrumbs
              icon="home"
              title={route[route.length - 1]}
              route={route}
              light={light}
              prefix={
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>
              }
            />
          </MDBox>

          {/* Upgrade Banner */}
          <UpgradeBanner />

          {/* [NEW] Free Trial Banner (Live Demo) */}
          <FreeTrialBanner show={showFreeTrialBanner} />

          {/* [NEW] Exit Live Demo Popup */}
          <ExitLiveDemo
            open={openExitDialog}
            onClose={() => setOpenExitDialog(false)} // Backdrop click -> Stay
            onConfirm={handleExitDemoConfirm}        // Start Free Trial
            onExit={handleExitDemoLogout}            // Exit Live Demo
          />

          {/* Right Side (Banner + Log out) */}
          {/* Right buttons */}
          <MDBox display="flex" alignItems="center" color={light ? "white" : "inherit"} ml={2}>
            {renderMenu()}
            {renderProfileMenu()}

            <MDBox ml={2}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                onClick={handleProfileMenuOpen}
                sx={navbarIconButton}
              >
                <Avatar
                  src={workspaceProfile?.profile_image}
                  alt={workspaceProfile?.name}
                  sx={{ width: 32, height: 32, bgcolor: "info.main" }}
                >
                  {workspaceProfile?.name ? workspaceProfile.name.charAt(0).toUpperCase() : "U"}
                </Avatar>
              </IconButton>
            </MDBox>
          </MDBox>

        </Toolbar>
      </AppBar>

      {/* Plan Dialog */}
      <Dialog
        open={openPlanDialog}
        onClose={() => setOpenPlanDialog(false)}
        fullWidth
        maxWidth="lg"
      >
        <Plan
          onSubscriptionUpdate={fetchWorkspaceSubscription}
          onClose={() => setOpenPlanDialog(false)}
        />
      </Dialog>
    </>
  );
}

// Setting default values for the props of DashboardNavbar
// DashboardNavbar.defaultProps = {
//   absolute: false,
//   light: false,
//   isMini: false,
// };

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
